import { useCallback, useEffect, useState, type FormEvent } from "react";
import { WalkFeedback } from "../WalkFeedback";
import { AnchorChip, formatNoteTime, useAnchorFocus } from "../doc/DocOverlay";
import { dropTextDraft, readTextDraft, writeTextDraft } from "../voice-draft";
import { VOICE_NOTE_KEYS, voiceNoteState, type VoiceNoteState } from "../voice-note";
import { actionIssueFrom, actionIssueFromThrown, actionIssueLocaleKey, type ActionIssue } from "../action-error";

// Typed comment on a delivered material, tucked behind a quiet link (Pavel
// 2026-07-23: the panel had two mic icons and a duplicate comments block -
// VOICE lives only in the walk orb, typing is the unobtrusive fallback).
export function FeedbackComposer(props: {
  t: (key: string) => string;
  sending: boolean;
  onSubmit: (payload: { text?: string | null }) => Promise<boolean>;
  // Where the unsent text is parked so it survives the tab closing (F98).
  // Keyed by the MATERIAL, so a new version still finds what was written.
  draftKey?: string;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);
  const [restored, setRestored] = useState(false);
  const draftKey = props.draftKey;

  // An unfinished sentence must not die with the screen (Pavel 2026-08-07).
  useEffect(() => {
    if (!draftKey) return;
    const saved = readTextDraft("deliverable", draftKey);
    if (!saved.trim()) return;
    setText(saved);
    setOpen(true);
    setRestored(true);
  }, [draftKey]);

  function edit(value: string) {
    setText(value);
    setRestored(false);
    if (draftKey) writeTextDraft("deliverable", draftKey, value);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;
    const delivered = await props.onSubmit({ text: trimmed });
    // The draft is dropped only once the note is really saved; a refused send
    // leaves the text exactly where the client can try again.
    if (delivered) {
      if (draftKey) dropTextDraft("deliverable", draftKey);
      setText("");
      setSent(true);
      setOpen(false);
    }
  }

  if (!open) {
    return (
      <div className="gate-feedback collapsed">
        <button className="quiet-link" type="button" onClick={() => { setOpen(true); setSent(false); }}>
          {props.t("sales.notes.writeToggle")}
        </button>
        {sent && <p className="message success-message" role="status">{props.t("sales.gate.feedbackSent")}</p>}
      </div>
    );
  }
  return (
    <form className="gate-feedback" onSubmit={(event) => void submit(event)}>
      {restored && <p className="message info-message walk-draft-restored" role="status">{props.t("voice.failure.textDraftRestored")}</p>}
      <textarea
        value={text}
        onChange={(event) => edit(event.target.value)}
        placeholder={props.t("sales.gate.feedbackPlaceholder")}
        rows={3}
        autoFocus
      />
      <div className="gate-feedback-actions">
        <button className="secondary-button" type="submit" disabled={props.sending || !text.trim()}>
          {props.sending ? props.t("sales.gate.sending") : props.t("sales.notes.saveNote")}
        </button>
        <button className="quiet-link" type="button" onClick={() => setOpen(false)}>{props.t("common.cancel")}</button>
      </div>
    </form>
  );
}

interface DeliverableNote {
  id: string;
  text: string | null;
  transcriptStatus?: string | null;
  audioUrl?: string | null;
  walk: boolean;
  anchors: string[];
  status: string;
  own: boolean;
  authorName: string | null;
  createdAt: string;
  resolvedAt?: string | null;
  // Server-computed truth of the running Alex edit: "sent" (queued),
  // "working" (editor picked it up), null (not part of a running edit).
  alexState?: "sent" | "working" | null;
  onVersion?: number;
  resolvedByNewer?: boolean;
}

// The client's side of the ONE note lifecycle: saved (partner sees it, still
// editable) -> sent (queued for Alex) -> working (editor picked it up) ->
// handled (worked into a version). Exported for the markup tests.
export type ClientNoteState = "saved" | "sent" | "working" | "handled" | "dismissed";

export function clientNoteState(note: Pick<DeliverableNote, "status" | "alexState">): ClientNoteState {
  // O9 (audit F115): a note the partner closed as NOT RELEVANT is not a note
  // that was worked into a new version. One chip for both would tell the
  // client their note is in the material when nobody put it there.
  if (note.status === "dismissed") return "dismissed";
  if (note.status !== "new") return "handled";
  return note.alexState ?? "saved";
}

const CLIENT_CHIP_CLASS: Record<ClientNoteState, string> = {
  saved: "is-saved",
  sent: "is-sent",
  working: "is-working",
  handled: "is-handled",
  dismissed: "is-dismissed"
};

// One card anatomy for every client note: head (time, author, walk badge,
// state chip), body (text or voice truth + player), foot (anchor chip,
// resolution time, delete). Exported so tests can pin the markup per state.
export function ClientNoteCard(props: {
  note: DeliverableNote;
  t: (key: string) => string;
  hasDoc?: boolean;
  busy?: boolean;
  onDelete?: (() => void) | null;
  onAnchorFocus?: () => void;
  onAnchorActivate?: () => void;
}) {
  const { note, t } = props;
  const state = clientNoteState(note);
  const showFoot = (props.hasDoc && note.anchors.length > 0)
    || (state === "handled" && note.resolvedAt)
    || Boolean(props.onDelete);
  return (
    <li className={`doc-note client-note state-${state}`}>
      <div className="doc-note-head">
        <time dateTime={note.createdAt}>{formatNoteTime(note.createdAt)}</time>
        {!note.own && note.authorName && <span className="doc-note-author">{note.authorName}</span>}
        {note.walk && <span className="doc-note-walk">{t("sales.notes.walkBadge")}</span>}
        <span className={`note-state-chip ${CLIENT_CHIP_CLASS[state]}`} title={t(`sales.notes.hint.${state}`)}>
          {t(`sales.notes.state.${state}`)}
        </span>
      </div>
      {note.text
        ? <p className="doc-note-text">{note.text}</p>
        : (
          <>
            <p className="doc-note-text doc-note-voice">
              {t(VOICE_NOTE_KEYS[voiceNoteState({ text: note.text, hasAudio: Boolean(note.audioUrl), transcriptStatus: note.transcriptStatus ?? null }) as Exclude<VoiceNoteState, "text">])}
            </p>
            {note.audioUrl && (
              <audio className="doc-note-audio" controls preload="none" src={note.audioUrl} aria-label={t("sales.notes.voicePlay")} />
            )}
          </>
        )}
      <p className="doc-note-state-hint">{t(`sales.notes.hint.${state}`)}</p>
      {showFoot && (
        <div className="doc-note-foot">
          {/* Instead of quoting what was pointed at, the chip SHOWS it:
              hover highlights the passage, click scrolls to it (Pavel
              2026-07-24: the text is for the AI, not for the reader). */}
          {props.hasDoc && note.anchors.length > 0 && (
            <AnchorChip
              count={note.anchors.length}
              label={t("sales.notes.anchorHint")}
              visibleLabel={t("sales.notes.anchorCount").replace("{count}", String(note.anchors.length))}
              onFocus={props.onAnchorFocus ?? (() => undefined)}
              onActivate={props.onAnchorActivate ?? (() => undefined)}
            />
          )}
          {state === "handled" && note.resolvedAt && (
            <time className="doc-note-resolved-at" dateTime={note.resolvedAt}>
              {t("sales.notes.resolvedAt").replace("{time}", formatNoteTime(note.resolvedAt))}
            </time>
          )}
          {props.onDelete && (
            <button className="quiet-link doc-note-delete" type="button" disabled={props.busy} onClick={props.onDelete}>
              {t("sales.notes.delete")}
            </button>
          )}
        </div>
      )}
    </li>
  );
}

// The saved notes stack under the composer: they are stored the moment they
// are made (nothing to "send" - the partner sees them when they open the
// client), and an own unprocessed note can be deleted (Pavel 2026-07-23).
// hasDoc: the reader shows our own document DOM, so anchor chips can light
// up and scroll to the pointed-at passage (same chips the partner has).
// While an Alex edit runs on this material the list polls, so the chips walk
// through the lifecycle before the client's eyes instead of freezing at
// "saved" (walk 2026-07-27).
export function DeliverableNotes(props: { deliverableId: string; csrfToken: string; t: (key: string) => string; version: number; hasDoc?: boolean }) {
  const [notes, setNotes] = useState<DeliverableNote[]>([]);
  const [alexActive, setAlexActive] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [failed, setFailed] = useState<ActionIssue | null>(null);
  const focusAnchor = useAnchorFocus();

  const load = useCallback(async () => {
    try {
      const response = await fetch(`/api/deliverables/${props.deliverableId}/feedback`, { credentials: "include" });
      if (!response.ok) return;
      const body = (await response.json()) as { notes: DeliverableNote[]; alexActive?: boolean };
      setNotes(body.notes ?? []);
      setAlexActive(Boolean(body.alexActive));
    } catch { /* list is best-effort */ }
  }, [props.deliverableId]);

  useEffect(() => { void load(); }, [load, props.version]);

  // Reconciliation poll: only while an edit runs (same ~8s cadence as the
  // partner's live loop), so "zapracovává se" flips to "zapracováno" by
  // itself when the new version lands. No reloads, no scroll jumps.
  useEffect(() => {
    if (!alexActive) return;
    const timer = window.setInterval(() => { void load(); }, 8000);
    return () => window.clearInterval(timer);
  }, [alexActive, load]);

  async function remove(noteId: string) {
    // Optimistic removal with rollback: the card disappears the moment the
    // client acts; a failed delete puts it back and says so.
    const snapshot = notes;
    setBusyId(noteId);
    setFailed(null);
    setNotes(notes.filter((note) => note.id !== noteId));
    try {
      const response = await fetch(`/api/deliverables/${props.deliverableId}/feedback/${encodeURIComponent(noteId)}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "X-Launchpad-CSRF": props.csrfToken }
      });
      if (!response.ok) {
        setNotes(snapshot);
        setFailed(await actionIssueFrom(response));
        return;
      }
      await load();
    } catch {
      setNotes(snapshot);
      setFailed(actionIssueFromThrown());
    } finally {
      setBusyId(null);
    }
  }

  if (notes.length === 0) return null;
  return (
    <ClientNotesPanel
      notes={notes}
      alexActive={alexActive}
      failed={failed}
      t={props.t}
      hasDoc={props.hasDoc}
      busyId={busyId}
      onDelete={(noteId) => void remove(noteId)}
      onAnchorFocus={(note) => focusAnchor(note.id, note.anchors, false)}
      onAnchorActivate={(note) => focusAnchor(note.id, note.anchors, true)}
    />
  );
}

// Pure presentation of the client notes panel (exported for tests and the
// fixture renders): header with the open count, ONE status banner (working
// beats worked-in), the card stack, and the flow hint that tells the client
// what happens next.
export function ClientNotesPanel(props: {
  notes: DeliverableNote[];
  alexActive: boolean;
  failed?: ActionIssue | null;
  t: (key: string) => string;
  hasDoc?: boolean;
  busyId?: string | null;
  onDelete?: (noteId: string) => void;
  onAnchorFocus?: (note: DeliverableNote) => void;
  onAnchorActivate?: (note: DeliverableNote) => void;
}) {
  const openCount = props.notes.filter((note) => note.status === "new").length;
  const workedIn = props.notes.some((note) => note.resolvedByNewer);
  const title = `${props.t("sales.notes.title")}${openCount > 0 ? ` (${openCount})` : ""}`;
  return (
    <section className="doc-notes" aria-label={title}>
      <header className="doc-notes-head">
        <h3>{title}</h3>
      </header>
      {props.alexActive ? (
        <p className="message doc-notes-working" role="status">
          <span className="loading-pulse" />
          <span className="doc-notes-working-copy">{props.t("sales.notes.workingBanner")}</span>
        </p>
      ) : workedIn ? (
        <p className="message success-message" role="status">{props.t("sales.notes.workedIn")}</p>
      ) : null}
      {props.failed && <p className="message error-message" role="alert">{props.t(actionIssueLocaleKey(props.failed))}</p>}
      <ol>
        {props.notes.map((note) => (
          <ClientNoteCard
            key={note.id}
            note={note}
            t={props.t}
            hasDoc={props.hasDoc}
            busy={props.busyId === note.id}
            onDelete={props.onDelete && note.own && clientNoteState(note) === "saved" ? () => props.onDelete?.(note.id) : null}
            onAnchorFocus={() => props.onAnchorFocus?.(note)}
            onAnchorActivate={() => props.onAnchorActivate?.(note)}
          />
        ))}
      </ol>
      <p className="doc-notes-hint">{props.t("sales.notes.flowHint")}</p>
    </section>
  );
}
